import React, { FC } from 'react';
import { FilterModel } from '../../../common/model/ui/filter.model';
import { UserInfoFields } from '../../../firebase/document-field.enums';
import OvListFilter from './OvListFilter';
import { FilterType } from '../../../common/model/ui/filter.type';
import { hasAppliedListFilters } from '../../../common/utils/helpers';
import { useQuery } from '@tanstack/react-query';
import UserService from '../../../services/user.service';
import { useTranslation } from 'react-i18next';
import { OvSortingButtonModel } from '../molecules/OvSortingButtonGroup';

const AGE_MIN = 0;
const AGE_MAX = 99;
const CYCLE_MIN = 0;
const CYCLE_MAX = 40;

export interface ProviderPatientFilterValues {
  knownReproductiveDisorder: string;
  shouldHideNotRegisteredUsers: string;
  isDemoAccount: string;
  /** Comma-joined "min,max" range, or '' when at default. */
  age: string;
  /** Comma-joined "min,max" range, or '' when at default. */
  averageCycleLength: string;
}

interface OvProviderPatientSearchFilterProps {
  filterValues: ProviderPatientFilterValues;
  sortingOptions: OvSortingButtonModel[];
  onHandleFilterValueChange: (filter: FilterModel) => void;
  onHandleSorting: (sortingValue: any) => void;
  removeFilters: () => void;
}

/** Parses a radio URL value ('true' | 'false' | '') back into the option value type. */
const parseRadioValue = (value: string): boolean | string => {
  if (value === 'true') {
    return true;
  }
  if (value === 'false') {
    return false;
  }
  return '';
};

/** Parses a comma-joined "min,max" range into a tuple, falling back to defaults. */
const parseRange = (
  value: string,
  min: number,
  max: number
): [number, number] => {
  const [low, high] = value.split(',');
  const parsedLow = parseInt(low, 10);
  const parsedHigh = parseInt(high, 10);

  return [
    Number.isNaN(parsedLow) ? min : parsedLow,
    Number.isNaN(parsedHigh) ? max : parsedHigh,
  ];
};

/**
 * Doctor/provider patient list search filter. Prop-driven (URL state owned by
 * `FilterablePatientList`) — the React Query replacement for the redux-backed
 * patient-list filters. Provider assignment is intentionally not part of this filter set.
 */
const OvProviderPatientSearchFilter: FC<OvProviderPatientSearchFilterProps> = ({
  filterValues,
  sortingOptions,
  onHandleFilterValueChange,
  onHandleSorting,
  removeFilters,
}) => {
  const { t } = useTranslation();

  const filters: FilterModel[] = [
    {
      fieldName: UserInfoFields.should_hide_not_registered_users,
      label: 'common.userSearchFilters.shouldHideNotRegisteredUsers',
      type: FilterType.radio,
      options: [
        { label: 'common.actions.yes', value: true },
        { label: 'common.actions.no', value: false },
        { label: 'common.actions.notSelected', value: '' },
      ],
      value: parseRadioValue(filterValues.shouldHideNotRegisteredUsers),
    },
    {
      fieldName: UserInfoFields.is_demo_account,
      label: 'common.userSearchFilters.showDemoAccounts',
      type: FilterType.radio,
      options: [
        { label: 'common.actions.yes', value: true },
        { label: 'common.actions.no', value: false },
        { label: 'common.actions.notSelected', value: '' },
      ],
      value: parseRadioValue(filterValues.isDemoAccount),
    },
    {
      fieldName: 'age',
      label: 'common.userFields.age',
      type: FilterType.slider,
      value: parseRange(filterValues.age, AGE_MIN, AGE_MAX),
      min: AGE_MIN,
      max: AGE_MAX,
      convertTo: 'age',
    },
    {
      fieldName: UserInfoFields.known_reproductive_disorder,
      label: 'common.userFields.fertilityDisorder',
      type: FilterType.select,
      options: [],
      value: filterValues.knownReproductiveDisorder,
    },
    {
      fieldName: UserInfoFields.length_of_average_monthly_cycle,
      label: 'common.userFields.averageCycleLength',
      type: FilterType.slider,
      value: parseRange(filterValues.averageCycleLength, CYCLE_MIN, CYCLE_MAX),
      min: CYCLE_MIN,
      max: CYCLE_MAX,
      convertTo: 'number',
    },
  ];

  const { data: disorders } = useQuery({
    queryKey: ['fertility-disorders'],
    queryFn: () => UserService.loadAllFertilityDisorders(),
    retry: 0,
  });

  const disordersFilter = filters.find(
    (filter) => filter.fieldName === UserInfoFields.known_reproductive_disorder
  );

  disordersFilter?.options?.push({
    label: t('common.values.none'),
    value: 'None',
  });

  disorders?.forEach((disorder) => {
    disordersFilter?.options?.push({
      label: disorder.known_reproductive_disorder,
      value: disorder.known_reproductive_disorder,
    });
  });

  disordersFilter?.options?.push({
    label: t('common.values.other'),
    value: 'Other',
  });

  return (
    <OvListFilter
      filters={filters}
      sortingOptions={sortingOptions}
      isOpened={true}
      hasAppliedFilters={hasAppliedListFilters(filters)}
      onRemoveFilters={removeFilters}
      onHandleSorting={onHandleSorting}
      onHandleFilterValueChange={onHandleFilterValueChange}
    />
  );
};

export default OvProviderPatientSearchFilter;
